import React, { useRef, useEffect, useCallback, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  /** Options list */
  options: SelectOption[];
  /** Controlled selected value */
  value?: string;
  /** Called with new value when an option is selected */
  onChange?: (value: string) => void;
  /** Placeholder text shown when no value selected */
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  // Index of the keyboard-focused option (-1 = none)
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value);

  // Close handler
  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  // Toggle open/close on trigger click
  const handleTriggerClick = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
    if (!open) setActiveIndex(-1);
  };

  // Keyboard navigation on the trigger (when closed) and on the menu (when open)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      if (!open) {
        // Open on Enter / Space / ArrowDown
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          setOpen(true);
          setActiveIndex(0);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          setActiveIndex((prev) => Math.min(prev + 1, options.length - 1));
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          setActiveIndex((prev) => Math.max(prev - 1, 0));
          break;
        }
        case 'Enter': {
          e.preventDefault();
          if (activeIndex >= 0 && activeIndex < options.length) {
            onChange?.(options[activeIndex].value);
            close();
            triggerRef.current?.focus();
          }
          break;
        }
        case 'Escape': {
          e.preventDefault();
          close();
          triggerRef.current?.focus();
          break;
        }
        case 'Tab': {
          close();
          break;
        }
      }
    },
    [open, activeIndex, options, onChange, close, disabled],
  );

  // Close on outside pointerdown
  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open, close]);

  // Close on Escape keydown at document level (covers cases where focus is elsewhere)
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, close]);

  // Scroll active item into view
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const menu = menuRef.current;
    if (!menu) return;
    const item = menu.children[activeIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  // Trigger: reuses Chip/Ghost button geometry per DESIGN_SYSTEM.md §Select recipe
  const triggerClasses = [
    'relative inline-flex items-center justify-between gap-2',
    // Design system glass: gatilho = campo (surface-field), 13px/500, sentence case.
    'w-full bg-snapfy-field border border-snapfy-border-field rounded-input',
    'text-snapfy-text-secondary font-medium text-[13px]',
    'px-3 py-2.5',
    'transition-colors duration-150',
    'hover:border-snapfy-border-strong hover:text-snapfy-text-primary',
    'focus-visible:outline-none focus-visible:ring-snapfy-focus',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Option base classes
  const optionBase =
    'w-full text-left px-2.5 py-2 rounded-control text-[12.5px] font-medium text-snapfy-text-primary ' +
    'transition-colors duration-150 cursor-pointer outline-none ' +
    'hover:bg-snapfy-primary-10';

  // Selected option classes (overrides base)
  const optionSelected =
    'bg-snapfy-primary-20 text-snapfy-text-accent';

  // Keyboard-active option (hover-like highlight for arrow navigation)
  const optionActive = 'bg-snapfy-primary-10';

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls="select-menu"
        disabled={disabled}
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
        className={triggerClasses}
      >
        <span className={selectedOption ? 'text-snapfy-text-primary' : ''}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>

        <ChevronDown
          size={14}
          aria-hidden="true"
          className={[
            'shrink-0 text-snapfy-text-secondary transition-transform duration-200',
            open ? 'rotate-180' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        />
      </button>

      {open && (
        <ul
          ref={menuRef}
          id="select-menu"
          role="listbox"
          aria-activedescendant={activeIndex >= 0 ? `select-opt-${activeIndex}` : undefined}
          onKeyDown={handleKeyDown as unknown as React.KeyboardEventHandler<HTMLUListElement>}
          tabIndex={-1}
          className={
            'absolute z-[50] mt-1.5 w-full ' +
            // Popover de vidro do DS.
            'snapfy-popover rounded-media p-1.5 ' +
            'list-none'
          }
        >
          {options.map((option, idx) => {
            const isSelected = option.value === value;
            const isKeyActive = idx === activeIndex;

            const optClasses = [
              optionBase,
              isSelected ? optionSelected : '',
              !isSelected && isKeyActive ? optionActive : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <li
                key={option.value}
                id={`select-opt-${idx}`}
                role="option"
                aria-selected={isSelected}
                className={optClasses}
                onPointerDown={(e) => {
                  // Prevent blur on trigger before we handle the selection
                  e.preventDefault();
                }}
                onClick={() => {
                  onChange?.(option.value);
                  close();
                  triggerRef.current?.focus();
                }}
                onMouseEnter={() => setActiveIndex(idx)}
              >
                {option.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Select;
